/**
 * S2S ChatBot - Speech-to-Speech Voice Conversation Component
 * 
 * This component manages real-time voice conversations with AWS Bedrock Nova Sonic.
 * 
 * Key Features:
 * - Real-time bidirectional audio streaming (coach speaks, student AI responds)
 * - Session rotation every 7.5 minutes to maintain conversation context
 * - Automatic feedback generation at the end of conversations
 * - Audio timing tracking for both participants

 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@cloudscape-design/components';
import { AlertDialog, Box, Button, Callout, Card, Flex, Grid, Heading, IconButton, Link, Popover, Text } from '@radix-ui/themes';
import './s2s.css';
import SlideOutPanel from './components/SlideOutPanel';
import S2sEvent from './helper/s2sEvents';
import { base64ToFloat32Array } from './helper/audioHelper';
import AudioPlayer from './helper/audioPlayer';
import { postConversation, getFeedbackWithConversation } from './helper/api';
import { messagesFixture, feedbackFixture, sessionIdFixture, timingFixture } from './util/fixtures';
import { capitalizeFirst } from "./util/string";
import { SESSION_PHASE } from './util/constants';
import { looksLikeCoachSpeech } from './util/roleDrift';
import { StudentDetails } from './components/StudentDetails';
import StatusMessages from './components/StatusMessages';
import TimingBar from './components/TimingBar';
import { useDebug } from './contexts/DebugContext';

/**
 * Map gender to voice ID for Nova Sonic
 */
function getVoiceByGender(gender) {
  if (!gender) return "matthew";

  const g = gender.toLowerCase();
  if (g === "female" || g === "f") return "tiffany";  
  if (g === "male" || g === "m") return "matthew";

  return "matthew";
}

/**
 * Build WebSocket URL based on current environment
 * Development: ws://localhost:8080/ws
 * Production: wss://domain.com/ws
 */
const getWebSocketUrl = () => {
  let url;
  const WS_PORT = process.env.WS_PORT || 8080;
  const { protocol, host, hostname, port } = window.location;
  const socketProto = protocol === 'https:' ? 'wss:' : 'ws:';

  if (port !== '' && process.env.NODE_ENV === 'development') {
    url = `${socketProto}//${hostname}:${WS_PORT}/ws`;
  } else {
    url = `${socketProto}//${host}/ws`;
  }

  return url;
};

/**
 * Calculate RMS (Root Mean Square) of audio buffer
 * Used for Voice Activity Detection (VAD)
 * Returns amplitude level (0.0 to 1.0+)
 */
function rmsFloat32(buf) {
  if (!buf || buf.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < buf.length; i++) {
    const x = buf[i];
    sum += x * x;
  }
  return Math.sqrt(sum / buf.length);
}

const S2sChatBot = (params) => {
  const debug = useDebug();
  const navigate = useNavigate();

  // ============================================================================
  // PROPS & CONSTANTS
  // ============================================================================
  
  const { title, scenario, student, systemPrompt, promptParams } = params;

  // Session rotates every 7.5 minutes to avoid Bedrock's context limits
  const ROTATE_AFTER_MS = 6.5 * 60_000;
  const ROTATE_JITTER_MS = 30_000;
  // Minimum conversation duration to generate feedback
  const MIN_SESSION_DURATION_SECONDS = 60;

  // Hard cap on session length, with a warning shown 5 minutes before the end.
  // Independent of the WebSocket rotation window (which resets every ~6.5 min).
  const MAX_SESSION_DURATION_MS = 30 * 60_000; // hard cap
  const WARNING_AT_MS = 25 * 60_000;           // warn 5 min before cap
  const WARNING_BANNER_VISIBLE_MS = 12_000;    // auto-dismiss the banner
  
  // Voice Activity Detection threshold for coach audio
  // Audio below this RMS level is considered silence
  const COACH_RMS_THRESHOLD = 0.015; 
  
  // Inactivity detection (coach silence)
  const INACTIVITY_AFTER_MS = 90_000;     // 30s for testing
  const INACTIVITY_CHECK_EVERY_MS = 500;  // watchdog frequency
  const STUDENT_SPEAKING_GRACE_MS = 3000; // don't popup while AI is actively speaking
  const INACTIVITY_DIALOG_TIMEOUT_SEC = 30;      // countdown popup (sec)
  // ============================================================================
  // STATE - UI and Session Management
  // ============================================================================
  
  const [alert, setAlert] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [backendReady, setBackendReady] = useState(false);
  const [backendActive, setBackendActive] = useState(true);
  const [sessionPhase, setSessionPhase] = useState(SESSION_PHASE.IDLE);
  const [chatMessages, setChatMessages] = useState({});
  const [audioChunks, setAudioChunks] = useState([]);
  const [includeChatHistory, setIncludeChatHistory] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [timing, setTiming] = useState(null);
  const [promptName, setPromptName] = useState(null);
  const [textContentName, setTextContentName] = useState(null);
  const [audioContentName, setAudioContentName] = useState(null);
  const [inputText, setInputText] = useState("");
  const [buttonIsDisabled, setButtonIsDisabled] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [remainingMs, setRemainingMs] = useState(MAX_SESSION_DURATION_MS); // live countdown to cap
  const [showTimeWarning, setShowTimeWarning] = useState(false);           // 5-min warning banner
  // ============================================================================
  // STATE - S2S Configuration
  // ============================================================================
  
  const [configSystemPrompt, setConfigSystemPrompt] = useState(S2sEvent.DEFAULT_SYSTEM_PROMPT);
  const [configTurnSensitivity, setConfigTurnSensitivity] = useState('MEDIUM');
  const [configToolUse, setConfigToolUse] = useState(JSON.stringify(S2sEvent.DEFAULT_TOOL_CONFIG, null, 2));
  const [configChatHistory, setConfigChatHistory] = useState(JSON.stringify(S2sEvent.DEFAULT_CHAT_HISTORY, null, 2));

  /**
   * Determine which system prompt to use
   * Priority: 1) API prop, 2) Config, 3) Default
   */
  const effectiveSystemPrompt = useMemo(() => {
    const apiPrompt = typeof systemPrompt === "string" ? systemPrompt.trim() : "";
    if (apiPrompt) return apiPrompt;

    const cfgPrompt = typeof configSystemPrompt === "string" ? configSystemPrompt.trim() : "";
    if (cfgPrompt) return cfgPrompt;

    return S2sEvent.DEFAULT_SYSTEM_PROMPT;
  }, [systemPrompt, configSystemPrompt]);

  // ============================================================================
  // REFS - Audio and WebSocket Management
  // ============================================================================
  
  // WebSocket connections
  const socketRef = useRef(null);                    // Current active WebSocket
  const nextSocketRef = useRef(null);                // Pre-warmed socket for rotation
  
  // Microphone audio capture
  const mediaRecorderRef = useRef(null);            // MediaRecorder for audio chunks
  const audioStreamRef = useRef(null);              // MediaStream from microphone
  const audioContextRef = useRef(null);             // AudioContext for processing
  const processorRef = useRef(null);                // ScriptProcessorNode for resampling
  
  // UI references
  const chatMessagesEndRef = useRef(null);          // For auto-scrolling chat
  const eventDisplayRef = useRef(null);             // Debug event display (optional)
  
  // Audio playback
  const audioPlayerRef = useRef(new AudioPlayer()); // Handles Bedrock audio output
  
  // State snapshot for callbacks
  const stateRef = useRef({});                      // Stable reference to current state
  
  // Timers and intervals
  const connectionFailureTimeoutRef = useRef(null); // WebSocket connection timeout
  const reminderIntervalRef = useRef(null);         // System prompt reminder interval
  const lastReminderAtRef = useRef(0);              // Last reminder timestamp
  const sendSystemReminderRef = useRef(null);       // Stable ref for reminder from message handler
  const lastDriftReminderAtRef = useRef(0);         // Throttle for coach-drift reminders
  
  // Session identifiers
  const readyDeferredRef = useRef(null);
  const promptNameRef = useRef(null); // Current Bedrock prompt ID

  // ============================================================================
  // REFS - Audio Timing Tracking
  // ============================================================================

  const studentAudioMsRef = useRef(0); // Total ms of student (AI) speech
  const coachAudioMsRef = useRef(0); // Total ms of coach (user) speech
  const studentSrRef = useRef(24000); // Student audio sample rate

  // ============================================================================
  // REFS - Session Rotation System
  // ============================================================================

  const rotateSessionProactivelyRef = useRef(null); // Rotation function reference
  const closingForRotateRef = useRef(false); // Flag: closing for rotation
  const isRotatingRef = useRef(false); // Flag: rotation in progress
  const rehydrateHistoryRef = useRef([]); // Chat history to rehydrate
  const rotateAfterMsRef = useRef(ROTATE_AFTER_MS);
  const rotateRequestedAtRef = useRef(0); // when rotation was requested (fallback timer)

  const rotationCheckIntervalRef = useRef(null); // Interval checking rotation time
  const rotateWindowStartAtRef = useRef(0); // Session start timestamp

  const lastCoachSpeechAtRef = useRef(0); // Last coach speech timestamp
  const lastStudentSpeechAtRef = useRef(0); // Last student speech timestamp
  const rotateRequestedRef = useRef(false); // Flag: rotation requested (unused but kept)
  const rotateAfterTurnEndRef = useRef(false); // Flag: rotate after turn ends
  const currentAssistantAudioIdRef = useRef(null); // Current assistant audio content ID

  const connectInFlightRef = useRef(false); // Flag: connection in progress
  const lastReconnectAtRef = useRef(0); // Last reconnection timestamp
  const wsOpenedAtRef = useRef(0); // WebSocket open timestamp
  const endSessionAndPersistRef = useRef(null);
  const retryCountRef = useRef(0);

  // Session time cap (wall-clock, survives WebSocket rotations)
  const sessionStartAtRef = useRef(0);        // set once per session in startSession
  const capCheckIntervalRef = useRef(null);   // 1s interval driving timer/warning/cap
  const capReachedRef = useRef(false);        // guard: end-at-cap fires once
  const warnedRef = useRef(false);            // guard: warning banner shows once
  const warningHideTimeoutRef = useRef(null); // banner auto-dismiss timeout


  // ============================================================================
  // EFFECT - Update State Snapshot
  // ============================================================================

  useEffect(() => {
    stateRef.current = {
      sessionStarted,
      promptName,
      audioContentName,
      textContentName,
      chatMessages,
      audioChunks,
      includeChatHistory,
      configChatHistory,
      alert,
      backendReady,
      backendActive
    };
  }, [
    sessionStarted,
    promptName,
    audioContentName,
    textContentName,
    chatMessages,
    audioChunks,
    includeChatHistory,
    configChatHistory,
    alert,
    backendReady,
    backendActive
  ]);

  // ============================================================================
  // HANDLERS - Navigation
  // ============================================================================

  const handleExitClick = (e) => {
    e.preventDefault();
    if (!debug) {
      setShowExitDialog(true);
    }
  };

  const handleConfirmExit = () => {
    navigate('/');
  };

  


  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const scrollToBottom = useCallback(() => {
    if (chatMessagesEndRef.current) {
      chatMessagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }
  }, []);

  // Format a millisecond duration as mm:ss for the live session timer
  const formatRemaining = (ms) => {
    const totalSec = Math.max(0, Math.ceil(ms / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const log = (msg, extra = {}) =>
    console.log(`[S2S] ${msg}`, {
      ws: socketRef.current?.readyState,
      rotating: isRotatingRef.current,
      closingForRotate: closingForRotateRef.current,
      prompt: stateRef.current?.promptName,
      ...extra
    });

  // ============================================================================
  // SESSION ROTATION SYSTEM
  // ============================================================================

  const startRotationController = useCallback(() => {
    if (rotationCheckIntervalRef.current) {
      clearInterval(rotationCheckIntervalRef.current);
      rotationCheckIntervalRef.current = null;
    }
    if (!sessionStarted) return;

    rotationCheckIntervalRef.current = setInterval(() => {
      if (!sessionStarted) return;
      if (isRotatingRef.current) return;

      const base = rotateWindowStartAtRef.current || Date.now();
      const elapsed = Date.now() - base;

      const rotateAfterMs = rotateAfterMsRef.current || ROTATE_AFTER_MS;

      // After rotateAfterMs, request rotation at next turn end
      if (elapsed >= rotateAfterMs) {
        if (!rotateAfterTurnEndRef.current) {
          rotateAfterTurnEndRef.current = true;
          rotateRequestedAtRef.current = Date.now();
          log('Rotation requested (wait for END_TURN)', { elapsed, rotateAfterMs });
        }
      }

      // Safety fallback: if END_TURN never arrives, rotate anyway
      if (rotateAfterTurnEndRef.current) {
        const requestedAt = rotateRequestedAtRef.current || 0;
        if (requestedAt > 0 && Date.now() - requestedAt > 15000) {
          rotateAfterTurnEndRef.current = false;
          rotateRequestedAtRef.current = 0;
          log('Rotation fallback fired (no END_TURN)', {});
          rotateSessionProactivelyRef.current?.();
        }
      }
    }, 250);
  }, [sessionStarted, ROTATE_AFTER_MS]);

  const stopRotationController = useCallback(() => {
    if (rotationCheckIntervalRef.current) {
      clearInterval(rotationCheckIntervalRef.current);
      rotationCheckIntervalRef.current = null;
    }
  }, []);

  const buildNovaHistoryFromChatMessages = useCallback(() => {
    const msgs = Object.entries(stateRef.current.chatMessages || {})
      .map(([_, msg]) => {
        if (!msg) return null;
        if ((msg.generationStage || '') !== 'FINAL') return null;

        const role = msg.role; // "USER" or "ASSISTANT"
        if (role !== 'USER' && role !== 'ASSISTANT') return null;

        const content = (msg.content || '').trim();
        if (!content) return null;

        // Skip ASSISTANT turns that already drifted into coaching — re-injecting
        // them on session rotation would reinforce the wrong persona.
        if (role === 'ASSISTANT' && looksLikeCoachSpeech(content)) {
          console.log('[S2S] Skipping drifted ASSISTANT turn from rotation history', {
            preview: content.slice(0, 80)
          });
          return null;
        }

        return { role, content };
      })
      .filter(Boolean);

    return msgs;
  }, []);

  const restoreStateFromStorage = useCallback(() => {
    try {
      const savedState = localStorage.getItem('s2s_saved_state');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        if (parsedState.includeChatHistory !== undefined) setIncludeChatHistory(parsedState.includeChatHistory);
        if (parsedState.configTurnSensitivity) setConfigTurnSensitivity(parsedState.configTurnSensitivity);
        if (parsedState.configSystemPrompt) setConfigSystemPrompt(parsedState.configSystemPrompt);
        if (parsedState.configToolUse) setConfigToolUse(parsedState.configToolUse);
        if (parsedState.configChatHistory) setConfigChatHistory(parsedState.configChatHistory);

        localStorage.removeItem('s2s_saved_state');
      }
    } catch (error) {
      console.error('Failed to restore state from localStorage:', error);
    }
  }, []);

  // ============================================================================
  // WEBSOCKET COMMUNICATION
  // ============================================================================

  const sendEvent = useCallback((event, targetSocket = null) => {
    const socket = targetSocket || socketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      const eventStr = JSON.stringify(event);
      socket.send(eventStr);
      event.timestamp = Date.now();

      if (eventDisplayRef.current) {
        eventDisplayRef.current.displayEvent(event, 'out');
      }
    } else {
      console.warn('Cannot send event - WebSocket not open. State:', socket?.readyState);
    }
  }, []);

  const cancelAudio = useCallback(() => {
    audioPlayerRef.current.bargeIn();
  }, []);

  /**
   * Handle incoming messages from Bedrock
   * IMPORTANT: we pass sourceWs so we can ignore prewarm socket events.
   */
  const handleIncomingMessage = useCallback(
    (message, sourceWs = null) => {
      // Ignore messages from prewarmed sockets
      if (sourceWs?.__prewarm) return;

      // If we have an active socket, ignore events from other sockets
      if (socketRef.current && sourceWs && sourceWs !== socketRef.current) return;

      const eventType = Object.keys(message?.event || {})[0];
      if (!eventType) return;

      // Backend readiness / health
      if (eventType === 'ready') {
        setBackendReady(true);
        setBackendActive(true);
        log('Backend ready');

        if (readyDeferredRef.current?.resolve) {
          const r = readyDeferredRef.current.resolve;
          readyDeferredRef.current = null;
          r();
        }
        return;
      }

      if (eventType === 'status') {
        const st = message.event.status || {};
        const active = !!st.active;
        const reason = st.reason || 'unknown';
        setBackendActive(active);

        if (active && reason === 'ready') {
          setBackendReady(true);
          log('Backend ready');

          if (readyDeferredRef.current?.resolve) {
            const r = readyDeferredRef.current.resolve;
            readyDeferredRef.current = null;
            r();
          }
          return;
        }

        if (!active) {
          log('Backend inactive', { reason });
          setBackendReady(false);

          const TRANSIENT_REASONS = ['timeout_error', 'transient_error', 'stream_ended'];
          const isTransient = TRANSIENT_REASONS.includes(reason);
          const isContextLimit = reason === 'context_limit_exceeded';

          // Always stop MediaRecorder and close socket
          try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
              mediaRecorderRef.current.stop();
            }
          } catch (e) {}

          if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            try { socketRef.current.close(); } catch (e) {}
          }

          // Transient error — rotate without closing mic/audio (max 2 retries)
          if (isTransient && retryCountRef.current < 2) {
            retryCountRef.current += 1;
            setIsRetrying(true);
            log(`Transient error (${reason}) — attempting rotation recovery`, { attempt: retryCountRef.current });
            setAlert(`Connection issue detected. Reconnecting... (attempt ${retryCountRef.current}/2)`);
            setBackendActive(true);  // ← agregar esto para evitar que aparezca el indicador del chat

            rehydrateHistoryRef.current = buildNovaHistoryFromChatMessages();
            closingForRotateRef.current = true;

            setTimeout(() => {
              closingForRotateRef.current = false;
              rotateSessionProactivelyRef.current?.();
            }, 1000);
            return;
          }

          // Terminal error or max retries reached — stop everything
          if (audioStreamRef.current) {
            try { audioStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (e) {}
            audioStreamRef.current = null;
          }

          if (processorRef.current) {
            try { processorRef.current.disconnect(); } catch (e) {}
            processorRef.current = null;
          }

          if (audioContextRef.current) {
            try {
              if (audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close();
              }
            } catch (e) {}
            audioContextRef.current = null;
          }

          retryCountRef.current = 0;
          setSessionStarted(false);
          setSessionPhase(SESSION_PHASE.IDLE);
          setBackendActive(false);

          const alertMsg = isContextLimit
            ? 'Maximum conversation length reached. Generating your feedback...'
            : isTransient
              ? 'Could not reconnect after 2 attempts. Generating feedback if available...'
              : `Session disconnected (${reason}). Generating feedback if available...`;

          setAlert(alertMsg);

          const messageCount = Object.keys(stateRef.current.chatMessages || {}).length;
          if (messageCount > 0) {
            endSessionAndPersistRef.current?.();
          }
        }
        return;
      }

      // Extract audio config if present
      if (eventType === 'contentStart') {
        const contentType = message.event.contentStart?.type;

        if (contentType === 'AUDIO') {
          const cfg = message.event.contentStart?.audioOutputConfiguration;
          if (cfg?.sampleRateHertz && Number.isFinite(cfg.sampleRateHertz)) {
            studentSrRef.current = cfg.sampleRateHertz;
          }
        }
      }

      // Extract common fields
      const role = message.event[eventType]?.role;
      const content = message.event[eventType]?.content;
      const contentId = message.event[eventType]?.contentId;
      const stopReason = message.event[eventType]?.stopReason;
      const contentType = message.event[eventType]?.type;

      switch (eventType) {
        case 'textOutput': {
          if (role === 'ASSISTANT' && typeof content === 'string' && content.startsWith('{')) {
            try {
              const evt = JSON.parse(content);
              if (evt?.interrupted === true) {
                cancelAudio();
              }
            } catch (e) {}
          }

          setChatMessages((prevMessages) => {
            const newMessages = { ...prevMessages };
            if (newMessages.hasOwnProperty(contentId)) {
              newMessages[contentId].content = content;
              newMessages[contentId].role = role;
              if (newMessages[contentId].raw === undefined) newMessages[contentId].raw = [];
              newMessages[contentId].raw.push(message);

              if (newMessages[contentId].raw.length > 1000) {
                newMessages[contentId].raw = newMessages[contentId].raw.slice(-1000);
              }
            }
            return newMessages;
          });

          // If the student model starts sounding like a coach, reinject the role
          // reminder immediately (throttled) so the next turn is more likely to recover.
          if (
            role === 'ASSISTANT' &&
            typeof content === 'string' &&
            looksLikeCoachSpeech(content)
          ) {
            const now = Date.now();
            if (now - lastDriftReminderAtRef.current > 15000) {
              lastDriftReminderAtRef.current = now;
              console.log('[S2S] Coach-tell detected in ASSISTANT text; sending role reminder', {
                preview: content.slice(0, 80)
              });
              sendSystemReminderRef.current?.(true);
            }
          }

          setTimeout(() => scrollToBottom(), 0);
          break;
        }

        case 'audioOutput': {
          try {
            const base64Data = message.event.audioOutput?.content;
            if (!base64Data) break;

            lastStudentSpeechAtRef.current = Date.now();
            const audioData = base64ToFloat32Array(base64Data);

            const sr = studentSrRef.current || 24000;
            const chunkMs = (audioData.length / sr) * 1000;

            if (Number.isFinite(chunkMs) && chunkMs > 0) {
              studentAudioMsRef.current += chunkMs;
            }

            audioPlayerRef.current.playAudio(audioData);
          } catch (error) {
            console.error('Error processing audio chunk:', error);
          }
          break;
        }

        case 'contentStart': {
          if (contentType === 'AUDIO' && role === 'ASSISTANT') {
            currentAssistantAudioIdRef.current = contentId;
          }

          if (contentType === 'TEXT') {
            let generationStage = '';
            if (message.event.contentStart.additionalModelFields) {
              try {
                generationStage = JSON.parse(message.event.contentStart.additionalModelFields)?.generationStage;
              } catch (e) {}
            }

            setChatMessages((prevMessages) => {
              const newMessages = { ...prevMessages };
              newMessages[contentId] = {
                content: '',
                role: role,
                generationStage: generationStage,
                raw: [],
                inputSource: 'audio'
              };
              newMessages[contentId].raw.push(message);

              if (newMessages[contentId].raw.length > 1000) {
                newMessages[contentId].raw = newMessages[contentId].raw.slice(-1000);
              }
              return newMessages;
            });

            setTimeout(() => scrollToBottom(), 0);
          }
          break;
        }

        case 'contentEnd': {
          if (contentType === 'AUDIO') {
            const isSame = contentId && contentId === currentAssistantAudioIdRef.current;
            const isEndTurn = stopReason === 'END_TURN';

            if (rotateAfterTurnEndRef.current && isSame && isEndTurn) {
              rotateAfterTurnEndRef.current = false;
              rotateRequestedAtRef.current = 0;

              setTimeout(() => {
                if (!isRotatingRef.current) rotateSessionProactivelyRef.current?.();
              }, 100);
            }
          }

          if (contentType === 'TEXT') {
            setChatMessages((prevMessages) => {
              const newMessages = { ...prevMessages };
              if (newMessages.hasOwnProperty(contentId)) {
                if (newMessages[contentId].raw === undefined) newMessages[contentId].raw = [];
                newMessages[contentId].raw.push(message);

                if (newMessages[contentId].raw.length > 1000) {
                  newMessages[contentId].raw = newMessages[contentId].raw.slice(-1000);
                }

                newMessages[contentId].stopReason = stopReason;
              }
              return newMessages;
            });

            setTimeout(() => scrollToBottom(), 0);
          }
          break;
        }

        default:
          break;
      }

      if (eventDisplayRef.current) {
        eventDisplayRef.current.displayEvent(message, 'in');
      }
    },
    [cancelAudio, scrollToBottom, buildNovaHistoryFromChatMessages, log]
  );

  const sendSystemReminder = useCallback(
    (force = false) => {
      const pn = stateRef.current.promptName || promptNameRef.current;
      if (!pn) return;

      if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) return;
      if (!stateRef.current.sessionStarted) return;

      const now = Date.now();
      if (!force && now - lastReminderAtRef.current < 4000) return;
      lastReminderAtRef.current = now;

      const contentName = crypto.randomUUID();

      sendEvent(S2sEvent.contentStartText(pn, contentName, 'USER', false));
      sendEvent(S2sEvent.textInput(pn, contentName, S2sEvent.ROLE_REMINDER));
      sendEvent(S2sEvent.contentEnd(pn, contentName));
    },
    [sendEvent]
  );

  useEffect(() => {
    sendSystemReminderRef.current = sendSystemReminder;
  }, [sendSystemReminder]);

  const softCloseTransport = useCallback(
    (opts = { bargeIn: false }) => {
      if (opts.bargeIn) {
        try {
          audioPlayerRef.current.bargeIn();
        } catch {}
      }

      try {
        if (stateRef.current.promptName && stateRef.current.audioContentName) {
          sendEvent(S2sEvent.contentEnd(stateRef.current.promptName, stateRef.current.audioContentName));
          sendEvent(S2sEvent.promptEnd(stateRef.current.promptName));
          sendEvent(S2sEvent.sessionEnd());
        }
      } catch (e) {
        console.warn('soft close sendEvent failed', e);
      }

      try {
        closingForRotateRef.current = true;
        socketRef.current?.close();
      } catch {}
    },
    [sendEvent]
  );

  const createWebSocket = useCallback(
    (isPreWarm = false) => {
      return new Promise((resolve, reject) => {
        const newPromptName = crypto.randomUUID();
        const newTextContentName = crypto.randomUUID();
        const newAudioContentName = crypto.randomUUID();

        const ws_url = getWebSocketUrl();
        const ws = new WebSocket(ws_url);

        ws.__prewarm = !!isPreWarm;

        const timeoutId = setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
          try {
            ws.close();
          } catch {}
        }, 10000);

        ws.onopen = () => {
          clearTimeout(timeoutId);
          log('WS open', { isPreWarm });
          ws.__openedAt = Date.now();

          if (!isPreWarm) {
            setBackendReady(false);
            setBackendActive(true);

            // Important: set active socket early to avoid "ready" race
            socketRef.current = ws;
          }

          if (!isPreWarm) {
            promptNameRef.current = newPromptName;
            setPromptName(newPromptName);
            setTextContentName(newTextContentName);
            setAudioContentName(newAudioContentName);
          }

          const modelId = 'amazon.nova-2-sonic-v1:0';

          sendEvent(S2sEvent.sessionStart(S2sEvent.DEFAULT_INFER_CONFIG, configTurnSensitivity, modelId), ws);

          let audioConfig = { ...S2sEvent.DEFAULT_AUDIO_OUTPUT_CONFIG };
          const gender = promptParams?.gender;
          audioConfig.voiceId = getVoiceByGender(gender);

          const toolConfig = configToolUse ? JSON.parse(configToolUse) : S2sEvent.DEFAULT_TOOL_CONFIG;

          sendEvent(S2sEvent.promptStart(newPromptName, audioConfig, toolConfig), ws);
          sendEvent(S2sEvent.contentStartText(newPromptName, newTextContentName, 'SYSTEM', false), ws);
          sendEvent(S2sEvent.textInput(newPromptName, newTextContentName, effectiveSystemPrompt), ws);
          sendEvent(S2sEvent.contentEnd(newPromptName, newTextContentName), ws);

          // Add static chat history if configured
          if (stateRef.current.includeChatHistory) {
            let chatHistory = JSON.parse(stateRef.current.configChatHistory);
            if (chatHistory === null) chatHistory = S2sEvent.DEFAULT_CHAT_HISTORY;
            for (const chat of chatHistory) {
              const chatHistoryContentName = crypto.randomUUID();
              sendEvent(S2sEvent.contentStartText(newPromptName, chatHistoryContentName, chat.role), ws);
              sendEvent(S2sEvent.textInput(newPromptName, chatHistoryContentName, chat.content), ws);
              sendEvent(S2sEvent.contentEnd(newPromptName, chatHistoryContentName), ws);
            }
          }

          // Rehydrate history from previous rotation
          if (Array.isArray(rehydrateHistoryRef.current) && rehydrateHistoryRef.current.length > 0) {
            for (const chat of rehydrateHistoryRef.current) {
              const cid = crypto.randomUUID();
              sendEvent(S2sEvent.contentStartText(newPromptName, cid, chat.role), ws);
              sendEvent(S2sEvent.textInput(newPromptName, cid, chat.content), ws);
              sendEvent(S2sEvent.contentEnd(newPromptName, cid), ws);
            }
            rehydrateHistoryRef.current = [];
          }

          // Only open the audio content block on the active socket.
          // Pre-warm sockets must NOT open it here — Bedrock would start expecting
          // audio immediately, but the mic is still sending to the old socket.
          // The caller (rotateSessionProactively / connectWebSocket) opens it after promotion.
          if (!isPreWarm) {
            sendEvent(S2sEvent.contentStartAudio(newPromptName, newAudioContentName), ws);
          }

          resolve({
            ws,
            promptName: newPromptName,
            textContentName: newTextContentName,
            audioContentName: newAudioContentName
          });
        };

        ws.onmessage = (message) => {
          const event = JSON.parse(message.data);
          handleIncomingMessage(event, ws);
        };

        ws.onerror = (error) => {
          clearTimeout(timeoutId);
          console.error('WebSocket Error: ', error);
          reject(error);
        };

        ws.onclose = (ev) => {
          const code = ev?.code;
          const reason = ev?.reason;
          const wasClean = ev?.wasClean;
          const livedMs = ws.__openedAt ? Date.now() - ws.__openedAt : 0;

          log('WS close', { code, reason, wasClean, livedMs, isPreWarm });

          if (isPreWarm) {
            log('WS close (prewarm) ignored', { code, livedMs });
            return;
          }

          if (!stateRef.current.sessionStarted) return;
          if (closingForRotateRef.current) {
            log('WS close ignored (rotate)', { code });
            return;
          }
          if (isRotatingRef.current) {
            log('WS close ignored (already rotating)', { code });
            return;
          }

          // If connection died immediately, don't try to rotate
          if (livedMs > 0 && livedMs < 2000) {
            log('WS died too fast -> stop (no rotate)', { code, livedMs });
            setAlert('Voice connection dropped immediately (check server/protocol).');
            setSessionStarted(false);
            return;
          }

          // Cooldown to prevent rotation loops
          const now = Date.now();
          if (now - lastReconnectAtRef.current < 3000) {
            log('WS close ignored (cooldown)', { code });
            return;
          }
          lastReconnectAtRef.current = now;

          // Unexpected close - trigger rotation
          log('WS close -> rotate', { code });
          rotateSessionProactivelyRef.current?.();
        };
      });
    },
    [
      configTurnSensitivity,
      configToolUse,
      sendEvent,
      handleIncomingMessage,
      effectiveSystemPrompt,
      promptParams?.gender,
      log
    ]
  );

  const connectWebSocket = useCallback(async () => {
    if (connectInFlightRef.current) {
      log('connectWebSocket skipped (in-flight)');
      return;
    }

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      log('connectWebSocket skipped (already open)');
      return;
    }

    connectInFlightRef.current = true;
    closingForRotateRef.current = false;

    try {
      const result = await createWebSocket(false);
      socketRef.current = result.ws; // redundant with onopen, but ok
      wsOpenedAtRef.current = Date.now();
    } catch (error) {
      setAlert('WebSocket Error: ' + error);
      console.error('WebSocket Error: ', error);
    } finally {
      connectInFlightRef.current = false;
    }
  }, [createWebSocket, log]);

  const waitForBackendReady = useCallback(() => {
    if (stateRef.current.backendReady && stateRef.current.backendActive) return Promise.resolve();
    return new Promise((resolve, reject) => {
      readyDeferredRef.current = { resolve, reject, ts: Date.now() };
      setTimeout(() => {
        if (readyDeferredRef.current && readyDeferredRef.current.resolve === resolve) {
          readyDeferredRef.current = null;
          reject(new Error('Backend ready timeout'));
        }
      }, 15000);
    });
  }, []);

  // ============================================================================
  // MICROPHONE AUDIO CAPTURE
  // ============================================================================

  const setupMicrophoneStream = useCallback(
    async () => {
      if (audioStreamRef.current && audioStreamRef.current.active) {
        log('Reusing existing audio stream');
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });

        audioStreamRef.current = stream;

        const audioContext = new (window.AudioContext || window.webkitAudioContext)({
          latencyHint: 'interactive'
        });

        audioContextRef.current = audioContext;

        const source = audioContext.createMediaStreamSource(stream);
        const processor = audioContext.createScriptProcessor(512, 1, 1);

        processorRef.current = processor;

        source.connect(processor);
        processor.connect(audioContext.destination);

        const targetSampleRate = 16000;

        processor.onaudioprocess = async (e) => {
          // Snapshot ANTES del await — captura el estado atómicamente en este momento
          const snap = {
            rotating: isRotatingRef.current,
            closing: closingForRotateRef.current,
            sessionStarted: stateRef.current.sessionStarted,
            backendReady: stateRef.current.backendReady,
            backendActive: stateRef.current.backendActive,
            promptName: stateRef.current.promptName,
            audioContentName: stateRef.current.audioContentName,
          };

          // Guard pre-await
          if (snap.rotating || snap.closing) return;
          if (!snap.sessionStarted || !snap.backendReady || !snap.backendActive) return;
          if (!snap.promptName || !snap.audioContentName) return;

          const inputBuffer = e.inputBuffer;

          const offlineContext = new OfflineAudioContext({
            numberOfChannels: 1,
            length: Math.ceil(inputBuffer.duration * targetSampleRate),
            sampleRate: targetSampleRate
          });

          const offlineSource = offlineContext.createBufferSource();
          const monoBuffer = offlineContext.createBuffer(1, inputBuffer.length, inputBuffer.sampleRate);
          monoBuffer.copyToChannel(inputBuffer.getChannelData(0), 0);

          offlineSource.buffer = monoBuffer;
          offlineSource.connect(offlineContext.destination);
          offlineSource.start(0);

          const renderedBuffer = await offlineContext.startRendering();

          // Guard post-await — usar el snapshot, NO stateRef.current
          // Si la rotación comenzó mientras esperábamos, descartar este chunk
          if (isRotatingRef.current || closingForRotateRef.current) return;

          const resampled = renderedBuffer.getChannelData(0);

          // Voice Activity Detection
          const level = rmsFloat32(resampled);
          const chunkMs = (resampled.length / targetSampleRate) * 1000;
          const now = Date.now();

          if (level > COACH_RMS_THRESHOLD) {
            lastCoachSpeechAtRef.current = now;
          }
          if (level > COACH_RMS_THRESHOLD && Number.isFinite(chunkMs) && chunkMs > 0) {
            coachAudioMsRef.current += chunkMs;
          }

          // Convert Float32 to Int16 PCM
          const buffer = new ArrayBuffer(resampled.length * 2);
          const pcmData = new DataView(buffer);

          for (let i = 0; i < resampled.length; i++) {
            const s = Math.max(-1, Math.min(1, resampled[i]));
            pcmData.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
          }

          // Convert to base64
          let binary = '';
          for (let i = 0; i < pcmData.byteLength; i++) {
            binary += String.fromCharCode(pcmData.getUint8(i));
          }

          // Usar el snapshot para garantizar coherencia — mismo promptName/audioContentName
          // que tenía el socket cuando comenzó el procesamiento de este chunk
          const event = S2sEvent.audioInput(snap.promptName, snap.audioContentName, btoa(binary));
          sendEvent(event);
        };


        log('Microphone stream setup complete');
      } catch (error) {
        console.error('Error setting up microphone: ', error);
        throw error;
      }
    },
    [sendEvent, log]
  );

  const startMicrophone = useCallback(
    async () => {
      try {
        await setupMicrophoneStream();

        if (mediaRecorderRef.current) {
          try {
            if (mediaRecorderRef.current.state === 'inactive') {
              mediaRecorderRef.current.start();
            }
          } catch (e) {
            console.warn('MediaRecorder already started or error:', e);
          }
        } else {
          mediaRecorderRef.current = new MediaRecorder(audioStreamRef.current);
          mediaRecorderRef.current.ondataavailable = (event) => {
            setAudioChunks((prev) => [...prev, event.data]);
          };
          mediaRecorderRef.current.start();
        }

        log('Microphone recording started/resumed');
      } catch (error) {
        console.error('Error accessing microphone: ', error);
        throw error;
      }
    },
    [setupMicrophoneStream, log]
  );

  // ============================================================================
  // SESSION LIFECYCLE
  // ============================================================================

  const startSession = useCallback(() => {
    coachAudioMsRef.current = 0;
    studentAudioMsRef.current = 0;
    studentSrRef.current = 24000;
    retryCountRef.current = 0;
    lastDriftReminderAtRef.current = 0;
    setIsRetrying(false);

    const now = Date.now();
    rotateWindowStartAtRef.current = now;
    rotateAfterMsRef.current = ROTATE_AFTER_MS + Math.floor(Math.random() * ROTATE_JITTER_MS);

    lastCoachSpeechAtRef.current = now;
    lastStudentSpeechAtRef.current = now;

    // Start the wall-clock cap timer: live countdown, 5-min warning, hard stop.
    sessionStartAtRef.current = now;
    capReachedRef.current = false;
    warnedRef.current = false;
    setShowTimeWarning(false);
    setRemainingMs(MAX_SESSION_DURATION_MS);
    if (capCheckIntervalRef.current) clearInterval(capCheckIntervalRef.current);
    capCheckIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - sessionStartAtRef.current;
      setRemainingMs(Math.max(0, MAX_SESSION_DURATION_MS - elapsed));

      if (elapsed >= WARNING_AT_MS && !warnedRef.current) {
        warnedRef.current = true;
        setShowTimeWarning(true);
        warningHideTimeoutRef.current = setTimeout(
          () => setShowTimeWarning(false), WARNING_BANNER_VISIBLE_MS);
      }
      if (elapsed >= MAX_SESSION_DURATION_MS && !capReachedRef.current) {
        capReachedRef.current = true;
        clearInterval(capCheckIntervalRef.current);
        capCheckIntervalRef.current = null;
        log('Session time cap reached — ending and generating feedback');
        endSessionAndPersistRef.current?.();
      }
    }, 1000);

    setFeedback(null);
    setSaveError(null);
    setChatMessages({});
    setSessionPhase(SESSION_PHASE.RUNNING);
    setSessionStarted(true);
    setSessionId(null);
    setBackendReady(false);  
    setBackendActive(true);   
    setAlert(null);

    setTimeout(() => scrollToBottom(), 0);
    if (eventDisplayRef.current) eventDisplayRef.current.cleanup();

    const initSession = async () => {
      try {
        await connectWebSocket();
        await waitForBackendReady();
        await startMicrophone();
      } catch (error) {
        console.error('Error starting session:', error);
        setAlert('Failed to start session');
      }
    };

    initSession();
  }, [scrollToBottom, connectWebSocket, waitForBackendReady, startMicrophone, ROTATE_AFTER_MS, ROTATE_JITTER_MS, MAX_SESSION_DURATION_MS, WARNING_AT_MS, WARNING_BANNER_VISIBLE_MS]);

  const endSession = useCallback(() => {
    if (connectionFailureTimeoutRef.current) {
      clearTimeout(connectionFailureTimeoutRef.current);
      connectionFailureTimeoutRef.current = null;
    }
    if (rotationCheckIntervalRef.current) {
      clearInterval(rotationCheckIntervalRef.current);
      rotationCheckIntervalRef.current = null;
    }
    if (capCheckIntervalRef.current) {
      clearInterval(capCheckIntervalRef.current);
      capCheckIntervalRef.current = null;
    }
    if (warningHideTimeoutRef.current) {
      clearTimeout(warningHideTimeoutRef.current);
      warningHideTimeoutRef.current = null;
    }
    setShowTimeWarning(false);
    if (socketRef.current) {
      if (mediaRecorderRef.current && stateRef.current.sessionStarted) {
        try {
          mediaRecorderRef.current.stop();
        } catch (e) {
          console.warn('MediaRecorder stop error:', e);
        }
      }

      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
        audioStreamRef.current = null;
      }

      if (processorRef.current) {
        try {
          processorRef.current.disconnect();
        } catch (e) {}
        processorRef.current = null;
      }

      if (audioContextRef.current) {
        try {
          if (audioContextRef.current.state !== 'closed') {  
            audioContextRef.current.close();
          }
        } catch (e) {}
        audioContextRef.current = null;
      }

      sendEvent(S2sEvent.contentEnd(stateRef.current.promptName, stateRef.current.audioContentName));
      sendEvent(S2sEvent.promptEnd(stateRef.current.promptName));
      sendEvent(S2sEvent.sessionEnd());

      socketRef.current.close();
      promptNameRef.current = null;

      setSessionStarted(false);
    }
  }, [sendEvent]);

  const buildConversationPayload = useCallback(() => {
    const msgs = Object.entries(chatMessages || {})
      .map(([contentId, msg]) => {
        if (!msg) return null;

        const stage = msg.generationStage || '';
        const role = msg.role || '';
        if (stage !== 'FINAL') return null;
        if (role !== 'USER' && role !== 'ASSISTANT') return null;

        const text = (msg.content || '').trim();
        if (!text) return null;

        const rawTs = Array.isArray(msg.raw) ? msg.raw.map((e) => e?.timestamp).filter(Boolean) : [];
        const tMin = rawTs.length ? Math.min(...rawTs) : Date.now();
        const tMax = rawTs.length ? Math.max(...rawTs) : tMin;

        const apiRole = role === 'USER' ? 'coach' : 'student';

        return {
          message_id: contentId,
          role: apiRole,
          content: text,
          time_start: new Date(tMin).toISOString(),
          time_end: new Date(tMax).toISOString(),
          _tMin: tMin,
          _tMax: tMax
        };
      })
      .filter(Boolean);

    msgs.sort((a, b) => a._tMin - b._tMin);

    const startMs = msgs.length ? msgs[0]._tMin : Date.now();
    const endMs = msgs.length ? msgs[msgs.length - 1]._tMax : startMs;

    // Extract Bedrock session ID from raw events
    let bedrockSessionId = null;
    for (const [, msg] of Object.entries(chatMessages || {})) {
      const raw = msg?.raw;
      if (!Array.isArray(raw)) continue;

      for (const ev of raw) {
        const eventType = ev?.event ? Object.keys(ev.event)[0] : null;
        const payload = eventType ? ev.event[eventType] : null;
        const sid = payload?.sessionId;
        if (sid) {
          bedrockSessionId = sid;
          setSessionId(sid);
          break;
        }
      }
      if (bedrockSessionId) break;
    }

    const userId = promptParams?.user_id || 'undefined';
    const scenarioId = promptParams?.scenario_id || 0;
    const studentId = promptParams?.student_id || 0;

    const sid = bedrockSessionId || stateRef.current.promptName;

    const textTotalMs = Math.max(0, endMs - startMs);

    const coachMs = coachAudioMsRef.current || 0;
    const studentMs = studentAudioMsRef.current || 0;
    const spokenMs = coachMs + studentMs;
    const totalMs = Math.max(textTotalMs, spokenMs);

    let coachPct = 0;
    let studentPct = 0;

    if (spokenMs > 0) {
      coachPct = (coachMs / spokenMs) * 100;
      const coachPct2 = Number(coachPct.toFixed(2));
      const studentPct2 = Number((100 - coachPct2).toFixed(2));
      coachPct = coachPct2;
      studentPct = studentPct2;
    }

    const apiMessages = msgs.map(({ _tMin, _tMax, ...rest }) => rest);

    return {
      session: {
        session_id: sid,
        user_id: userId,
        scenario_id: scenarioId,
        student_id: studentId,
        status: 'completed',
        start_ts: new Date(startMs).toISOString(),
        end_ts: new Date(endMs).toISOString(),
        timing: {
          duration_total_sec: Math.round(totalMs / 1000),
          duration_coach_sec: Math.round(coachMs / 1000),
          duration_student_sec: Math.round(studentMs / 1000),
          duration_coach_pct: coachPct,
          duration_student_pct: studentPct
        }
      },
      messages: apiMessages
    };
  }, [chatMessages, promptParams]);

  const rotateSessionProactively = useCallback(
    async () => {
      if (!stateRef.current.sessionStarted) return;
      if (isRotatingRef.current) return;

      isRotatingRef.current = true;
      log('Starting fast rotation');

      if (rotationCheckIntervalRef.current) {
        clearInterval(rotationCheckIntervalRef.current);
        rotationCheckIntervalRef.current = null;
      }

      try {
        setBackendReady(false);
        setBackendActive(true);

        // 1. Capture history for rehydration
        rehydrateHistoryRef.current = buildNovaHistoryFromChatMessages();

        // 2. Pre-warm next WebSocket
        log('Pre-warming next WebSocket');
        const nextWsPromise = createWebSocket(true);

        // 3. Close current Bedrock session
        log('Closing current session');
        try {
          if (stateRef.current.promptName && stateRef.current.audioContentName) {
            sendEvent(S2sEvent.contentEnd(stateRef.current.promptName, stateRef.current.audioContentName));
            sendEvent(S2sEvent.promptEnd(stateRef.current.promptName));
            sendEvent(S2sEvent.sessionEnd());
          }
        } catch (e) {
          console.warn('Session close events failed:', e);
        }

        closingForRotateRef.current = true;
        try {
          socketRef.current?.close();
        } catch (e) {
          console.warn('Socket close failed:', e);
        }

        // 4. Wait for pre-warmed WebSocket
        log('Waiting for pre-warmed WebSocket');
        const result = await nextWsPromise;

        // 5. Swap to new connection
        socketRef.current = result.ws;
        socketRef.current.__prewarm = false;

        promptNameRef.current = result.promptName;
        setPromptName(result.promptName);
        setTextContentName(result.textContentName);
        setAudioContentName(result.audioContentName);

        await waitForBackendReady();

        // Now that the socket is promoted and backend is ready, open the audio content block.
        // This is intentionally after waitForBackendReady to guarantee Bedrock
        // has processed sessionStart/promptStart before receiving contentStartAudio.
        sendEvent(S2sEvent.contentStartAudio(result.promptName, result.audioContentName));

        // 6. Reset timers
        const now = Date.now();
        wsOpenedAtRef.current = now;
        rotateWindowStartAtRef.current = now;
        rotateAfterMsRef.current = ROTATE_AFTER_MS + Math.floor(Math.random() * ROTATE_JITTER_MS);

        lastCoachSpeechAtRef.current = now;
        lastStudentSpeechAtRef.current = now;

        // 7. Resume rotation checking
        startRotationController();
        setIsRetrying(false);
        setAlert(null);
        log('Fast rotation complete');
      } catch (e) {
        console.error('Rotation failed:', e);
        setAlert('Session rotation failed - reconnecting...');

        try {
          await connectWebSocket();
          await waitForBackendReady();
          await startMicrophone();
        } catch (reconnectError) {
          console.error('Reconnect failed:', reconnectError);
          setSessionStarted(false);
        }
      } finally {
        isRotatingRef.current = false;
        closingForRotateRef.current = false;
      }
    },
    [
      buildNovaHistoryFromChatMessages,
      createWebSocket,
      sendEvent,
      startRotationController,
      connectWebSocket,
      waitForBackendReady,
      startMicrophone,
      ROTATE_AFTER_MS,
      ROTATE_JITTER_MS,
      log
    ]
  );

  useEffect(() => {
    rotateSessionProactivelyRef.current = rotateSessionProactively;
  }, [rotateSessionProactively]);

  useEffect(() => {
    if (sessionStarted) startRotationController();
    else stopRotationController();

    return () => stopRotationController();
  }, [sessionStarted, startRotationController, stopRotationController]);

  const endSessionAndPersist = useCallback(
    async () => {
      if (isSaving) return;

      setIsSaving(true);
      setSaveError(null);

      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
        audioStreamRef.current = null;
      }

      if (processorRef.current) {
        try {
          processorRef.current.disconnect();
        } catch (e) {}
        processorRef.current = null;
      }

      if (audioContextRef.current) {
        try {
          if (audioContextRef.current.state !== 'closed') {  
            audioContextRef.current.close();
          }
        } catch (e) {}
        audioContextRef.current = null;
      }

      try {
        endSession();
      } catch (e) {
        console.warn('endSession failed', e);
      }

      setSessionStarted(false);

      if (Object.keys(chatMessages).length === 0) {
        setIsSaving(false);
        return;
      }

      const payload = buildConversationPayload();
      setTiming(payload.session.timing);

      const sid = payload?.session?.session_id;
      const durationSec = payload?.session?.timing?.duration_total_sec ?? 0;

      if (durationSec < MIN_SESSION_DURATION_SECONDS) {
        setSessionPhase(SESSION_PHASE.COMPLETED);

        setFeedback({
          feedback_summary: `Not enough evidence to generate feedback. The conversation must be at least ${MIN_SESSION_DURATION_SECONDS} seconds long. Please start a new conversation.`,
          strengths: [],
          opportunities_for_growth: [],
          behavior_scores: [],
          mal: {
            planning: {
              gaps: [
                {
                  gap: 'Insufficient evidence to identify a gap',
                  rationale: `The conversation was shorter than ${MIN_SESSION_DURATION_SECONDS} seconds, so there is not enough coach speech to identify a performance gap.`,
                  primary: true
                }
              ]
            },
            learning: {
              focus_skill: '',
              practice_suggestion: ''
            },
            assessing: {
              success_criteria: ''
            },
            adjusting: {
              next_session_commitment: ''
            }
          }
        });

        setIsSaving(false);
        return;
      }

      try {
        if (sid) {
          const feedbackPayload = Object.assign({ scenario, student }, payload);
          setSessionPhase(SESSION_PHASE.GETTING_FEEDBACK);

          const response = await getFeedbackWithConversation(feedbackPayload);
          setFeedback(response.feedback);

          setSessionPhase(SESSION_PHASE.SAVING_CONVERSATION);
          await postConversation(payload);

          setSessionPhase(SESSION_PHASE.COMPLETED);
        } else {
          throw new Error('No session_id found to request feedback');
        }
      } catch (err) {
        setSaveError(err?.message || String(err));
      } finally {
        setIsSaving(false);
      }
    },
    [isSaving, chatMessages, scenario, student, endSession, buildConversationPayload]
  );

  const handleSessionChange = useCallback(() => {
    if (sessionStarted) {
      endSessionAndPersist();
    } else {
      startSession();
    }
  }, [sessionStarted, startSession, endSessionAndPersist]);
  const submitText = useCallback(() => {
    if (!inputText || !inputText.trim() || !sessionStarted) {
      return;
    }

    const newTextContentName = crypto.randomUUID();

    setChatMessages((prevMessages) => {
      const newMessages = { ...prevMessages };
      newMessages[newTextContentName] = {
        content: inputText,
        role: 'USER',
        generationStage: '',
        raw: [],
        inputSource: 'text'
      };
      return newMessages;
    });

    sendEvent(S2sEvent.contentStartText(promptName, newTextContentName, 'USER', true));
    sendEvent(S2sEvent.textInput(promptName, newTextContentName, inputText));
    sendEvent(S2sEvent.contentEnd(promptName, newTextContentName));

    setInputText('');
    setTimeout(() => scrollToBottom(), 0);
  }, [inputText, sessionStarted, promptName, sendEvent, scrollToBottom]);

  const handleSendText = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        submitText();
      }
    },
    [submitText]
  );

  const handleShowFeedback = () => {
    setIsPanelOpen(!isPanelOpen);
  };

  const handleMenuTap = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // ============================================================================
  // EFFECTS - Lifecycle Management
  // ============================================================================

  useEffect(() => {
    if (reminderIntervalRef.current) {
      clearInterval(reminderIntervalRef.current);
      reminderIntervalRef.current = null;
    }

    if (!sessionStarted) return;

    sendSystemReminder(true);

    reminderIntervalRef.current = setInterval(() => {
      sendSystemReminder(false);
    }, 60000);

    return () => {
      if (reminderIntervalRef.current) {
        clearInterval(reminderIntervalRef.current);
        reminderIntervalRef.current = null;
      }
    };
  }, [sessionStarted, sendSystemReminder]);

  useEffect(() => {
    restoreStateFromStorage();

    audioPlayerRef.current.start().catch((err) => {
      console.error('Failed to initialize audio player:', err);
    });

    setTimeout(() => {
      scrollToBottom();
    }, 100);

    return () => {
      audioPlayerRef.current.stop();

      if (capCheckIntervalRef.current) {
        clearInterval(capCheckIntervalRef.current);
        capCheckIntervalRef.current = null;
      }
      if (warningHideTimeoutRef.current) {
        clearTimeout(warningHideTimeoutRef.current);
        warningHideTimeoutRef.current = null;
      }

      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (processorRef.current) {
        try {
          processorRef.current.disconnect();
        } catch (e) {}
      }
      if (audioContextRef.current) {
        try {
          if (audioContextRef.current.state !== 'closed') {  
            audioContextRef.current.close();
          }
        } catch (e) {
          audioContextRef.current = null;
        }
      }
    };
  }, [restoreStateFromStorage, scrollToBottom]);

  useEffect(() => {
    const messageCount = Object.keys(chatMessages || {}).length;
    if (messageCount > 0) {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [chatMessages, scrollToBottom]);

  useEffect(() => {
    endSessionAndPersistRef.current = endSessionAndPersist;
  }, [endSessionAndPersist]);

  useEffect(() => {
    setButtonIsDisabled(
      sessionPhase === SESSION_PHASE.SAVING_CONVERSATION || sessionPhase === SESSION_PHASE.GETTING_FEEDBACK
    );
  }, [sessionPhase]);

  useEffect(() => {
    if (feedback) {
      setTimeout(() => {
        setIsPanelOpen(true);
      }, 300);
    }
  }, [feedback]);

  // UNCOMMENT TO POPULATE WITH FIXTURE DATA, DONT COMMIT WITH THESE LINES UNCOMMENTED
  useEffect(() => {
    if (debug) {
      setChatMessages(messagesFixture);
      setFeedback(feedbackFixture);
      setSessionId(sessionIdFixture);
      setTiming(timingFixture);
    }
  }, [debug]);

  const renderMessage = (msg, key) => {
    if (msg.role === 'ASSISTANT' && msg.generationStage === 'FINAL') return '';
    return (
      <div className="item" key={key}>
        <div className={msg.role === 'USER' ? 'user' : 'bot'}>
          <Icon name={msg.role === 'USER' ? 'user-profile' : 'gen-ai'} />
          &nbsp;&nbsp;
          {msg.role === 'USER' && (
            <Icon name={msg.inputSource === 'text' ? 'edit' : 'microphone'} className="input-source-icon" />
          )}
          &nbsp;
          {capitalizeFirst(msg.content)}
        </div>
      </div>
    );
  };

  const colorFeedback = (score) => {
    let color = 'green';
    if (score <= 3) color = 'yellow';
    if (score < 2) color = 'red';
    return (
      <Text color={color}>
        (Score: {score})
      </Text>
    );
  };

  // ============================================================================
  // RENDER - UI Components
  // ============================================================================

  return (
    <>
      <Box className="status-msg-wrap">
        <StatusMessages sessionPhase={sessionPhase} saveError={saveError} alert={alert} />
      </Box>

      <div className="s2s">

        <Box className="mobile-topbar">
          <Flex justify="between" align="center" className="menu-button">
            <IconButton onClick={handleMenuTap}>
              <Icon name="menu" />
            </IconButton>
            <Flex align="center" gap="3">
              {feedback && (
                <Link onClick={handleShowFeedback} mr="2">
                  Show Feedback
                </Link>
              )}
              {sessionStarted && (
                <Text size="1" weight="bold" color={remainingMs <= 5 * 60_000 ? 'red' : 'gray'}>
                  {formatRemaining(remainingMs)}
                </Text>
              )}
              <Button variant="primary" onClick={handleSessionChange} disabled={buttonIsDisabled}>
                <Icon
                  name={sessionStarted ? 'microphone-off' : 'microphone'}
                  title={sessionStarted ? 'End Conversation' : 'Start Conversation'}
                />
              </Button>
            </Flex>
          </Flex>
        </Box>

        <Box className="mobile-sidebar">
          <SlideOutPanel
            isOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
            width="65vw"
            side="left"
            showTitle={false}
          >
            <Box p="2">
              <Link href="/" className="home-link" onClick={handleExitClick}>
                <Text weight="bold">&laquo; Back to scenario selection</Text>
              </Link>
              <StudentDetails sessionId={sessionId} student={student} />
            </Box>
          </SlideOutPanel>
        </Box>

        <Grid columns="2fr 8fr" gap="4" width="100%" className="topbar">
          <Flex align="center" px="4">
            <Link href="/" className="home-link" onClick={handleExitClick}>
              <Text weight="bold">
                &laquo; Back<span className="hide-laptop"> to scenario selection</span>
              </Text>
            </Link>
          </Flex>
          <Flex justify="between" align="center" pr="4" py="2">
            <Heading size="5">{title}</Heading>
            <Box>
              {feedback && (
                <Button onClick={handleShowFeedback} mr="4">
                  Show Feedback
                </Button>
              )}
              <Flex align="center" gap="4" style={{ display: 'inline-flex' }}>
                {sessionStarted && (
                  <Text size="2" weight="bold" color={remainingMs <= 5 * 60_000 ? 'red' : 'gray'}>
                    {formatRemaining(remainingMs)}
                  </Text>
                )}
                <Button variant="primary" onClick={handleSessionChange} disabled={buttonIsDisabled}>
                  <Icon name={sessionStarted ? 'microphone-off' : 'microphone'} />
                  <Text>{sessionStarted ? 'End Conversation' : 'Start Conversation'}</Text>
                </Button>
              </Flex>
            </Box>
          </Flex>
        </Grid>

        <Grid
          columns={{
            initial: '1',
            sm: '2fr 8fr'
          }}
          gap="4"
          width="100%"
        >
          <Box p="4" className="sidebar">
            <StudentDetails sessionId={sessionId} student={student} />
          </Box>

          <Box my="4" mr="4" ml={{ initial: '4', sm: '0' }}>
            {showTimeWarning && (
              <Callout.Root color="yellow" variant="surface" mb="3">
                <Callout.Icon>
                  <Icon name="status-warning" />
                </Callout.Icon>
                <Callout.Text>
                  5 minutes left in this session. It will end automatically and your feedback report will be generated.
                </Callout.Text>
              </Callout.Root>
            )}
            {sessionStarted && backendReady && backendActive && Object.keys(chatMessages).length === 0 && (
              <Callout.Root color="blue" variant="surface" mb="3">
                <Callout.Icon>
                  <Icon name="status-info" />
                </Callout.Icon>
                <Callout.Text>
                  You speak first — say hello to start the conversation.
                </Callout.Text>
              </Callout.Root>
            )}
            <div className="conversation-container">
              <div className="chatarea">
                {Object.keys(chatMessages).map((key) => {
                  const msg = chatMessages[key];
                  return renderMessage(msg, key);
                })}
                {/* Connection status inside chat area */}
                {sessionStarted && !isRetrying && (!backendReady || !backendActive) && (
                  <div className="chat-status-indicator">
                    {backendActive
                      ? <><span className="chat-spinner" /> <span>Connecting to voice session...</span></>
                      : <span>⚠️ Session disconnected. Reconnecting...</span>
                    }
                  </div>
                )}
                <div className="endbar" ref={chatMessagesEndRef}></div>
              </div>
              <div className="input-container" style={{ display: 'none' }}>
                <div className="input-wrapper">
                  <input
                    type="text"
                    className="inputtext"
                    placeholder="Send a message to Nova Sonic"
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleSendText}
                    value={inputText}
                    disabled={!sessionStarted}
                  />
                  <Button
                    variant="primary"
                    className="submit-button"
                    onClick={submitText}
                    disabled={!sessionStarted || !inputText || !inputText.trim()}
                  >
                    <Icon name="angle-up" />
                  </Button>
                </div>
              </div>
            </div>
          </Box>
        </Grid>

        <SlideOutPanel isOpen={isPanelOpen} onClose={() => setIsPanelOpen(false)} width="65vw" side="right">
          {feedback && (
            <Box p={{ initial: '2', sm: '3' }}>
              {timing && <TimingBar timing={timing} />}

              <Box p="2">
                <Heading size="4" mb="3">
                  Summary
                </Heading>
                <Text as="p">{feedback.feedback_summary}</Text>
              </Box>

              {/* Hide MAL entirely for legacy feedback without `mal` (no crash). */}
              {feedback?.mal && (
                <Box p="2">
                  <Heading size="4" mb="1">
                    Master Adaptive Learner
                  </Heading>
                  <Text as="p" size="2" color="gray" mb="3">
                    Your learning plan for this coaching session
                  </Text>

                  <Heading size="3" mb="2">
                    Planning
                  </Heading>
                  <ul className="fb-items">
                    {(feedback.mal.planning?.gaps || []).map((g, i) => (
                      <li key={i}>
                        <Flex gap="2" align="baseline" wrap="wrap">
                          <Text weight="bold">{g.gap}</Text>
                          {g.primary ? (
                            <Text size="1" weight="bold" color="blue">
                              Primary
                            </Text>
                          ) : null}
                        </Flex>
                        {g.rationale ? (
                          <Text as="p" mt="1">
                            {g.rationale}
                          </Text>
                        ) : null}
                      </li>
                    ))}
                  </ul>

                  {/* Omit empty Learning/Assessing/Adjusting (insufficient-evidence uses ""). */}
                  {(feedback.mal.learning?.focus_skill || feedback.mal.learning?.practice_suggestion) && (
                    <>
                      <Heading size="3" mt="4" mb="2">
                        Learning
                      </Heading>
                      {feedback.mal.learning?.focus_skill ? (
                        <Box mb="2">
                          <Text weight="bold">Focus skill:</Text>{' '}
                          <Text>{feedback.mal.learning.focus_skill}</Text>
                        </Box>
                      ) : null}
                      {feedback.mal.learning?.practice_suggestion ? (
                        <Box>
                          <Text weight="bold" as="p">
                            Practice suggestion:
                          </Text>
                          <Text as="p">{feedback.mal.learning.practice_suggestion}</Text>
                        </Box>
                      ) : null}
                    </>
                  )}

                  {feedback.mal.assessing?.success_criteria ? (
                    <>
                      <Heading size="3" mt="4" mb="2">
                        Assessing
                      </Heading>
                      <Box>
                        <Text weight="bold" as="p">
                          Success criteria:
                        </Text>
                        <Text as="p">{feedback.mal.assessing.success_criteria}</Text>
                      </Box>
                    </>
                  ) : null}

                  {feedback.mal.adjusting?.next_session_commitment ? (
                    <>
                      <Heading size="3" mt="4" mb="2">
                        Adjusting
                      </Heading>
                      <Box>
                        <Text weight="bold" as="p">
                          Next-session commitment:
                        </Text>
                        <Text as="p">{feedback.mal.adjusting.next_session_commitment}</Text>
                      </Box>
                    </>
                  ) : null}
                </Box>
              )}

              {feedback?.strengths?.length > 0 && (
                <Box p="2">
                  <Heading size="4" mb="3">
                    Strengths
                  </Heading>
                  <ul className="fb-items">
                    {(feedback.strengths || []).map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </Box>
              )}

              {feedback?.opportunities_for_growth?.length > 0 && (
                <Box p="2">
                  <Heading size="4" mb="3">
                    Opportunities for growth
                  </Heading>
                  <ul className="fb-items">
                    {(feedback.opportunities_for_growth || []).map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </Box>
              )}

              {feedback?.behavior_scores?.length > 0 && (
                <>
                  <Heading mb="4" mt="6" ml="2">
                    <Flex align="baseline" gap="4">
                      <Text>Behavioral Scores</Text>
                      <Popover.Root>
                        <Popover.Trigger>
                          <Link size="2" style={{ cursor: 'pointer' }}>
                            What do these ratings mean? <Icon name="status-info" />
                          </Link>
                        </Popover.Trigger>
                        <Popover.Content side="top">
                          <Text as="p" color="red">
                            <Text weight="bold">1</Text> = Harmful / missing fundamentals
                          </Text>
                          <Text as="p" color="yellow">
                            <Text weight="bold">2</Text> = Inconsistent, mostly missing
                          </Text>
                          <Text as="p" color="yellow">
                            <Text weight="bold">3</Text> = Adequate, some gaps
                          </Text>
                          <Text as="p" color="green">
                            <Text weight="bold">4</Text> = Strong, minor gaps
                          </Text>
                          <Text as="p" color="green">
                            <Text weight="bold">5</Text> = Excellent, consistently demonstrated
                          </Text>
                        </Popover.Content>
                      </Popover.Root>
                    </Flex>
                  </Heading>

                  <Grid
                    columns={{
                      initial: '1',
                      sm: '2'
                    }}
                    gap="3"
                  >
                    {(feedback?.behavior_scores || []).map((score, i) => (
                      <Card key={i}>
                        <Heading size="4" mb="3">
                          {score.behavior_name} {colorFeedback(score.score)}
                        </Heading>
                        <Box px="3" mb="3">
                          <Text weight="bold">Evidence:</Text>
                          <ul className="fb-items">
                            {(score.evidence || []).map((evidence, j) => (
                              <li key={j}>{evidence}</li>
                            ))}
                          </ul>
                        </Box>
                        <Box px="3">
                          <Text weight="bold" as="p">
                            Suggestion:
                          </Text>
                          <Text as="p">{score.suggestion}</Text>
                        </Box>
                      </Card>
                    ))}
                  </Grid>
                </>
              )}
            </Box>
          )}
        </SlideOutPanel>

        <AlertDialog.Root open={showExitDialog} onOpenChange={setShowExitDialog}>
          <AlertDialog.Content maxWidth="450px">
            <AlertDialog.Title>Leave Session?</AlertDialog.Title>
            <AlertDialog.Description size="2">
              Leaving now will end your session. Your conversation and feedback will not be saved. Are you sure you want to
              continue?
            </AlertDialog.Description>

            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button variant="solid" color="red" onClick={handleConfirmExit}>
                  Leave Session
                </Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>
      </div>
    </>
  );
};

export default S2sChatBot;
